/**
 * Markdown with a live preview, side by side.
 *
 * The preview is the same renderer the saved note uses, so what you see is
 * what is stored — and it escapes before it formats, so a note body cannot
 * inject markup into the page it is previewed on.
 */

import { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { renderMarkdown } from '@/lib/markdown';
import { cn } from '@/lib/utils';

export function MarkdownEditor({
  value,
  onChange,
  rows = 6,
  placeholder,
  autoFocus = false,
}: {
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  placeholder?: string;
  autoFocus?: boolean;
}): React.ReactElement {
  const [showPreview, setShowPreview] = useState(true);

  return (
    <div className="space-y-1">
      <div className="flex justify-end">
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline underline-offset-2"
          onClick={() => setShowPreview((current) => !current)}
        >
          {showPreview ? 'Hide preview' : 'Show preview'}
        </button>
      </div>
      <div className={cn('grid gap-2', showPreview ? 'sm:grid-cols-2' : 'grid-cols-1')}>
        <Textarea
          rows={rows}
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder ?? 'What happened, what changed, what is next…'}
          onChange={(event) => onChange(event.target.value)}
          aria-label="Note body (Markdown)"
        />
        {showPreview && (
          <div
            aria-label="Preview"
            className="min-h-[80px] space-y-1 overflow-auto rounded-md border bg-muted/30 p-2 text-sm"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(value) }}
          />
        )}
      </div>
    </div>
  );
}
